import { useMemo, type FC } from "react"

import type {
  PowerlineFeatureProps,
  PowerlineSpanFeatureProps,
  PowerlineTowerFeatureProps,
  DeviceMarker,
  GeoPopupMetadata,
} from "types/geolocation.types"

import { CardPopup } from "widgets/styled/map"
import DevicePopup from "./DevicePopup"
import PowerlinePopup from "./PowerlinePopup"
import SpanPopup from "./SpanPopup"
import TowerPopup from "./TowerPopup"

// Leaflet fixes the content box to this width.
const CARD_WIDTH = 320

interface GeoPopupProps {
  type: "powerline" | "device" | "span" | "tower"
  data:
    | DeviceMarker
    | PowerlineFeatureProps
    | PowerlineSpanFeatureProps
    | PowerlineTowerFeatureProps
  hideButton?: boolean
  metadata?: GeoPopupMetadata
}

const GeoPopup: FC<GeoPopupProps> = ({ data, type, hideButton, metadata }) => {
  const PopupContent = useMemo(() => {
    switch (type) {
      case "device": {
        return <DevicePopup data={data as DeviceMarker} hideButton={hideButton} />
      }
      case "powerline": {
        return (
          <PowerlinePopup data={data as PowerlineFeatureProps} hideButton={hideButton} />
        )
      }
      case "span": {
        return (
          <SpanPopup
            data={data as PowerlineSpanFeatureProps}
            lastLimiterAar={metadata?.span?.aar}
            lastLimiterDlr={metadata?.span?.dlr}
            peaks={metadata?.span?.peaks}
            lineSpanIds={metadata?.span?.lineSpanIds}
            hideButton={hideButton}
          />
        )
      }
      case "tower": {
        return (
          <TowerPopup data={data as PowerlineTowerFeatureProps} hideButton={hideButton} />
        )
      }
      default: {
        return <></>
      }
    }
  }, [type, data, hideButton, metadata])

  if (!data) return null

  // Leaflet mounts a popup's content only once it opens, so the popups' own
  // queries wait for that rather than running for every marker on the map.
  return (
    <CardPopup closeButton={false} minWidth={CARD_WIDTH} maxWidth={CARD_WIDTH}>
      <div data-testid="geo-popup">{PopupContent}</div>
    </CardPopup>
  )
}

export default GeoPopup
